import { CustomerPermissionCodes, UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    CompanyPermissionEntity,
    EntitySetName,
    ICompanyPermissionEntity,
    ICompanyRoleEntity,
    ICompanyRolePermissionEntity
} from "@odata/GeneratedEntityTypes";
import { CompanyPermissionCode, CompanyRoleTypeCode, FeatureCode } from "@odata/GeneratedEnums";
import { sortByPredefinedOrder } from "@utils/general";
import React, { Component } from "react";

import BindingContext from "../../../odata/BindingContext";
import TestIds from "../../../testIds";
import memoizeOneWithParamsCheck from "../../../utils/memoizeOne";
import { FormStorage } from "../../../views/formView/FormStorage";
import { PermissionsGrid } from "../generalRoles/GeneralRoles.styles";
import { PermissionSwitch } from "../generalRoles/PermissionsTable";

const CustomerRolePermissionOrder: CompanyPermissionCode[] = [
    CustomerPermissionCodes.PrAttendance,
    CustomerPermissionCodes.CompanyInfo,
    // TODO: here "Zadavani vlastní docházky" when ready
    CustomerPermissionCodes.AccountingReports,
    CustomerPermissionCodes.PrPayrollAgenda,
    CustomerPermissionCodes.DocumentApproval,
    CustomerPermissionCodes.PrPayrollInfoConfidential,
    CustomerPermissionCodes.Payments,
    CustomerPermissionCodes.PrPayrollInfo,
    CustomerPermissionCodes.ChatInteractWithAccountant,
];

const UserRolePermissionOrder: CompanyPermissionCode[] = [
    UserPermissionCodes.Asset,
    UserPermissionCodes.Audit,
    UserPermissionCodes.Bank,
    UserPermissionCodes.CanImport,
    UserPermissionCodes.CashBox,
    UserPermissionCodes.CustomerPortal,
    UserPermissionCodes.DataBox,
    UserPermissionCodes.DocumentPosting,
    UserPermissionCodes.FiscalYearClose,
    UserPermissionCodes.ChatTakeover,
    UserPermissionCodes.InBox,
    UserPermissionCodes.InternalDocuments,
    UserPermissionCodes.InvoicesIssued,
    UserPermissionCodes.InvoicesReceived,
    UserPermissionCodes.MasterData,
    UserPermissionCodes.PrPayroll,
    UserPermissionCodes.PrPayrollSetting,
    UserPermissionCodes.ReceivableAdjustments,
    UserPermissionCodes.RecurringTask,
    UserPermissionCodes.Reports,
    UserPermissionCodes.VatStatement,
];

const PermissionOrderByRoleType: Record<CompanyRoleTypeCode, CompanyPermissionCode[]> = {
    [CompanyRoleTypeCode.CustomerRole]: CustomerRolePermissionOrder,
    [CompanyRoleTypeCode.UserRole]: UserRolePermissionOrder,
};

interface IProps {
    roleTypeCode: CompanyRoleTypeCode;
    storage: FormStorage;
    roleId: number;
}

class CompanyPermissionsTable extends Component<IProps> {
    componentDidMount = async (): Promise<void> => {
        const storage = this.props.storage as FormStorage;

        if (storage.loaded && storage.data.bindingContext.isNew() && !storage.data.entity.CompanyRolePermissions) {
            await this.init();
        }
    };

    // componentDidMount can be called during storage.init call
    // and CompanyRolePermissions would get deleted from the storage later
    // => we need to wait for the storage to be loaded
    componentDidUpdate = async (): Promise<void> => {
        const storage = this.props.storage as FormStorage;

        if (storage.loaded && storage.data.bindingContext.isNew() && !this.props.storage.data.entity.CompanyRolePermissions) {
            await this.init();
        }
    };

    init = async (): Promise<void> => {
        const permissions = await this.fetchPermissions();

        this.props.storage.data.entity.CompanyRolePermissions = permissions?.map((perm: ICompanyPermissionEntity) => ({
            Id: BindingContext.NEW_ENTITY_ID_PROP,
            CompanyPermissionCode: perm.Code,
            CompanyPermission: perm
        }));
        this.forceUpdate();
    };

    fetchPermissions = memoizeOneWithParamsCheck(async (): Promise<ICompanyPermissionEntity[]> => {
        const result = await this.props.storage.oData.getEntitySetWrapper(EntitySetName.CompanyPermissions)
            .query()
                .select(CompanyPermissionEntity.Code, CompanyPermissionEntity.Name, CompanyPermissionEntity.Description, CompanyPermissionEntity.FeatureCode, CompanyPermissionEntity.CompanyRoleTypeCode)
            .fetchData<ICompanyPermissionEntity[]>();

        return result.value;
    });

    handleChange = (code: string, checked: boolean): void => {
        this.props.storage.handleFirstChange();

        const permissions = this.props.storage.data.entity.CompanyRolePermissions;
        const permission = permissions.find((role: ICompanyRolePermissionEntity) => role.CompanyPermission.Code === code);
        permission.IsEnabled = checked;
        if (permission.OwnOnly === true && checked === false) {
            permission.OwnOnly = false;
        }
        this.forceUpdate();
    };

    onOwnOnlyChange = (code: string, ownOnly: boolean): void => {
        this.props.storage.handleFirstChange();

        const permissions = this.props.storage.data.entity.CompanyRolePermissions;
        const permission = permissions.find((role: ICompanyRolePermissionEntity) => role.CompanyPermission.Code === code);
        permission.OwnOnly = ownOnly;
        this.forceUpdate();
    };

    render() {
        const { storage } = this.props;
        const entity = storage.data.entity as ICompanyRoleEntity;
        const permissions = entity.CompanyRolePermissions?.filter((p: ICompanyRolePermissionEntity) => {
            if (p.CompanyPermission.CompanyRoleTypeCode !== this.props.roleTypeCode) {
                return false;
            }
            const featureCode = p.CompanyPermission.FeatureCode as FeatureCode;
            return !featureCode || storage.context.isFeatureSwitchEnabled(featureCode);
        }) ?? [];

        sortByPredefinedOrder<ICompanyRolePermissionEntity, CompanyPermissionCode>(
            PermissionOrderByRoleType[this.props.roleTypeCode], p => p.CompanyPermission.Code as CompanyPermissionCode)(permissions);

        return <PermissionsGrid data-testid={TestIds.PermissionsGrid}>
            {permissions.map((role: ICompanyRolePermissionEntity) => {
                return <PermissionSwitch
                    key={role.CompanyPermission.Code}
                    permission={role.CompanyPermission}
                    checked={role.IsEnabled}
                    ownOnly={role.OwnOnly}
                    storage={storage}
                    onOwnOnlyChange={this.onOwnOnlyChange}
                    isReadOnly={storage.isReadOnly}
                    isDisabled={storage.isDisabled}
                    onChange={this.handleChange}/>;
            })}
        </PermissionsGrid>;
    }
}

export default CompanyPermissionsTable;